import { useEffect, useRef } from 'react';

/**
 * useScrollReveal — safe version
 *
 * KEY DESIGN DECISION: elements start VISIBLE (opacity:1).
 * The hook ADDS the class "will-animate" to elements that are
 * OFF-SCREEN, then adds "revealed" when they scroll into view.
 * Elements already in the viewport on mount are NEVER hidden.
 *
 * This guarantees content is NEVER invisible even if JS is slow,
 * the intersection observer fails, or timing issues occur.
 */
export function useScrollReveal() {
  const ref = useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    let io;
    let mo;
    let debounceTimer;

    const setupObserver = () => {
      if (io) io.disconnect();

      io = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('revealed');
              entry.target.classList.remove('will-animate');
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.05, rootMargin: '0px 0px 0px 0px' }
      );

      const vh = window.innerHeight;
      root.querySelectorAll('[data-reveal]').forEach(el => {
        if (el.classList.contains('revealed')) return;
        const rect = el.getBoundingClientRect();
        if (rect.top < vh - 20) {
          // Already visible — reveal immediately, no animation
          el.classList.add('revealed');
        } else {
          // Off-screen — mark for animation and observe
          el.classList.add('will-animate');
          io.observe(el);
        }
      });
    };

    // Run immediately
    setupObserver();

    // Re-run when new DOM nodes are added (async API data)
    mo = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(setupObserver, 30);
    });
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      if (io) io.disconnect();
      if (mo) mo.disconnect();
      clearTimeout(debounceTimer);
    };
  }, []);

  return ref;
}

export function useSingleReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight) {
      el.classList.add('revealed');
      return;
    }
    el.classList.add('will-animate');
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.classList.add('revealed');
        el.classList.remove('will-animate');
        io.disconnect();
      }
    }, { threshold: 0.05 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

export function animateCounter(el, target, duration = 1400, suffix = '') {
  if (!el) return;
  const start = performance.now();
  const step = (now) => {
    const p = Math.min((now - start) / duration, 1);
    const e = 1 - Math.pow(1 - p, 3);
    el.textContent = (Number.isInteger(target) ? Math.round(target * e) : (target * e).toFixed(1)) + suffix;
    if (p < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}
